import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { CalendarOptions, DateSpanApi, FullCalendarComponent } from '@fullcalendar/angular';
import engb from '@fullcalendar/core/locales/en-gb';
import { Router } from '@angular/router';
import { debounceTime, switchMap } from 'rxjs/operators';
import { ISupplierScheduleFilter } from '../../../interfaces/supplier-schedule-filter';
import { from, Subject } from 'rxjs';
import {
  ISupplierSchedule,
  SupplierAvailabilityStateEnum,
} from '../../../interfaces/supplier-schedule';
import { MessageService } from 'primeng';
import { Message } from '../../../classes/Message';
import { SupplierScheduleService } from '../../../services';
import { flatten } from '../../../utils/flatten';
import moment from 'moment';

enum CalendarEventType {
  SupplierAvailability = 0,
  SupplierTask = 1,
  PastEvent = 2,
}

const stateConfig = {
  [SupplierAvailabilityStateEnum.unset]: {
    title: 'Unset',
    backgroundColor: '#FFBF00',
    className: 'icon-unknown',
  },
  [SupplierAvailabilityStateEnum.unavailable]: {
    title: 'Unavailable',
    backgroundColor: '#e0284f',
    className: 'icon-unavailable',
  },
  [SupplierAvailabilityStateEnum.available]: {
    title: 'Available',
    backgroundColor: '#4CAF50',
    className: 'icon-available',
  },
  [SupplierAvailabilityStateEnum.doNotDisturb]: {
    title: 'Do Not Disturb',
    backgroundColor: '#e0284f',
    className: 'icon-dnd',
  },
};

@Component({
  selector: 'app-weekly-calendar',
  templateUrl: './app-weekly-calendar.component.html',
  styleUrls: ['./app-weekly-calendar.component.scss'],
})
export class AppWeeklyCalendarComponent implements OnInit, OnDestroy {
  @ViewChild('calendar') calendarComponent: FullCalendarComponent;
  public supplierSchedule: ISupplierSchedule;
  public events: any[] = [];
  availabilityStates = SupplierAvailabilityStateEnum;

  @Input() supplierId: number;
  @Output() onSupplierTodayDNDChanged = new EventEmitter<boolean>();

  calendarOptions: CalendarOptions = {
    initialView: 'dayGridWeek',
    headerToolbar: false,
    height: 'auto',
    timeZone: 'UTC',
    locale: engb,
    selectable: true,
    events: this.events,
    moreLinkClick: 'popover',
    views: {
      agenda: {
        eventLimit: 2,
      },
    },
    dateClick: this.handleDateClick.bind(this),
    eventClick: this.calendarEventChanged.bind(this),
    select: this.calendarDateClicked.bind(this),
    viewDidMount: this.calendarCreated.bind(this),
    datesSet: this.visibleDatesChanged.bind(this),
    eventDidMount: function (info) {
      $(info.el).attr('pTooltip', info.event.extendedProps.description);
      $(info.el).attr('tooltipPosition', 'top');
    },
    selectAllow: (selectInfo) => this.allowUserToChangeDateAvailability(selectInfo),
    eventContent: (arg) => {
      if (arg.event.extendedProps.type == SupplierAvailabilityStateEnum.doNotDisturb) {
        return {
          html: '<i class="fa fa-minus-circle"></i> Do Not Disturb',
        };
      }
      return arg.event.title;
    },
  };

  showRangeSelectionDialog: boolean = false;
  selectedDateRangeStart: Date;
  selectedDateRangeEnd: Date;
  selectedDateRangeEndDisplay: Date;
  private calendarViewChangedSubject = new Subject<ISupplierScheduleFilter>();
  availabilityFilterVal: ISupplierScheduleFilter;

  constructor(
    private router: Router,
    private messageService: MessageService,
    private scheduleService: SupplierScheduleService,
  ) {}

  ngOnInit() {
    this.initSupplierAvailabilityFilter();
    this.setupCalendarSubscription();
  }

  ngOnDestroy() {
    this.calendarViewChangedSubject.complete();
  }

  private setupCalendarSubscription() {
    this.calendarViewChangedSubject
      .pipe(
        switchMap((filter) => from(this.scheduleService.getSupplierAvailability(flatten(filter)))),
        debounceTime(500),
      )
      .subscribe(
        (result) => {
          if (result) {
            this.supplierSchedule = result;

            this.setSupplierIsDoNotDisturb();
            this.setEvents(result);
          } else {
            this.messageService.clear();
            this.messageService.add(
              Message.Error('We were unable to fetch a valid schedule', 'Invalid Request'),
            );
          }
        },
        (err) => {
          this.messageService.add(Message.Error('Error loading data', 'Request Error'));
        },
      );
  }

  setSupplierIsDoNotDisturb() {
    // set var to true if supplier availability is set to do not disturb for today's date
    const isDND = this.supplierSchedule.supplierAvailabilityExceptions.some(
      (x) =>
        x.availabilityState == SupplierAvailabilityStateEnum.doNotDisturb &&
        moment(x.exceptionDate).isSame(moment(), 'day'),
    );

    this.onSupplierTodayDNDChanged.emit(isDND);
  }

  private initSupplierAvailabilityFilter() {
    this.availabilityFilterVal = {
      first: 0,
      rows: 7,
      sortField: '',
      sortOrder: 1,
      filters: {
        fromDate: { value: moment().startOf('week').toDate() },
        toDate: { value: moment().endOf('week').toDate() },
        supplierId: { value: this.supplierId || 0 },
        dayOrder: { value: null },
        orderPickup: { value: null },
        scheduleSaturation: { value: 0 },
        contractTypeIds: { value: null },
        organisationTagIds: { value: null },
        serviceDeliveryGroupIds: { value: null },
        engineiusRegionIds: { value: null },
        engineiusAreaIds: { value: null },
      },
    };
  }

  private getNextState(currentState: SupplierAvailabilityStateEnum): SupplierAvailabilityStateEnum {
    const isVme = this.isVMEUser();
    const transitions = {
      [SupplierAvailabilityStateEnum.unset]: SupplierAvailabilityStateEnum.available,
      [SupplierAvailabilityStateEnum.available]: SupplierAvailabilityStateEnum.unavailable,
      [SupplierAvailabilityStateEnum.unavailable]: SupplierAvailabilityStateEnum.doNotDisturb,
      [SupplierAvailabilityStateEnum.doNotDisturb]: isVme
        ? SupplierAvailabilityStateEnum.unset
        : SupplierAvailabilityStateEnum.available, // unset maybe?
    };
    return transitions[currentState];
  }

  private allowUserToChangeDateAvailability(selectInfo: DateSpanApi): boolean {
    const calendarApi = this.calendarComponent.getApi();
    const startInterval = calendarApi.view.currentStart;
    const endInterval = calendarApi.view.currentEnd;

    return (
      moment(selectInfo.start).startOf('day').isSameOrAfter(moment.utc().startOf('day')) &&
      moment(selectInfo.start).startOf('day').isSameOrAfter(moment(startInterval).startOf('day')) &&
      moment(selectInfo.end).endOf('day').isSameOrBefore(moment(endInterval).endOf('day'))
    );
  }

  setEvents(supplierSchedule: ISupplierSchedule) {
    const startDate = moment(this.availabilityFilterVal.filters.fromDate.value);
    const endDate = moment(this.availabilityFilterVal.filters.toDate.value);
    const durationDays = Math.abs(moment.duration(startDate.diff(endDate)).asDays());

    const schedule = Array(durationDays)
      .fill(null)
      .map((_, index) => {
        const currentDate = moment.utc(startDate).add(index, 'days');
        const exception = supplierSchedule.supplierAvailabilityExceptions.find((ex) =>
          moment(ex.exceptionDate).isSame(currentDate, 'day'),
        );

        const state = exception?.availabilityState ?? SupplierAvailabilityStateEnum.unset;
        const config = stateConfig[state];

        return {
          title: config.title,
          start: currentDate.startOf('day').toDate(),
          end: currentDate.endOf('day').toDate(),
          backgroundColor: config.backgroundColor,
          className: `${currentDate.isBefore(moment()) ? 'in-past ' : ''}${config.className}`,
          eventId: exception?.id ?? -1,
          allDay: true,
          jobId: null,
          calendarEventType: currentDate.isSameOrAfter(moment(), 'day')
            ? CalendarEventType.SupplierAvailability
            : CalendarEventType.PastEvent,
          state,
          extendedProps: {
            type: state,
          },
        };
      });

    const supplierTasks = supplierSchedule.supplierTasks.map((task) => ({
      title: `${task.jobNumber} - ${task.regNum}`,
      start: moment
        .utc(task.taskSupplierSchedule?.specifiedPickUpTime ?? task.dropoffLatestDateTime)
        .toDate(),
      end: moment
        .utc(task.taskSupplierSchedule?.specifiedDropOffTime ?? task.dropoffLatestDateTime)
        .toDate(),
      backgroundColor: 'grey',
      className: '',
      eventId: -1,
      allDay: false,
      jobId: task.jobId,
      calendarEventType: CalendarEventType.SupplierTask,
    }));

    this.events = [...schedule, ...supplierTasks];
    this.rerenderCalendar();
  }

  private rerenderCalendar() {
    this.calendarOptions.events = this.events;
    this.calendarComponent?.getApi()?.render();
  }

  calendarCreated({ view }) {
    this.calendarViewChanges(view);
  }

  visibleDatesChanged(calendarEvent): void {
    this.calendarViewChanges(calendarEvent.view);
  }

  calendarViewChanges(view: any) {
    this.availabilityFilterVal.filters.fromDate.value = view.currentStart;
    this.availabilityFilterVal.filters.toDate.value = view.currentEnd;
    this.calendarViewChangedSubject.next(this.availabilityFilterVal);
  }

  handleDateClick(arg) {}

  calendarEventChanged(event: any) {
    const calendarEventData = event.event;

    //console.log('Calendar event: ', calendarEventData);

    if (
      calendarEventData.extendedProps.calendarEventType === CalendarEventType.SupplierAvailability
    ) {
      const currentState = calendarEventData.extendedProps.state;

      /* Updated WIP-90 No longer Not able to unset.
      // vme users can't unset dnd state
      const isVME = this.isVMEUser();
      if (isVME && currentState == SupplierAvailabilityStateEnum.doNotDisturb) {
        return;
      }*/

      const nextState = this.getNextState(currentState);

      // cannot set DND or Unavailable as the state when there are tasks on the date
      if (
        (nextState == SupplierAvailabilityStateEnum.doNotDisturb ||
          nextState == SupplierAvailabilityStateEnum.unavailable) &&
        this.supplierSchedule.supplierTasks.some((x) => {
          const taskStart = moment.utc(
            x.taskSupplierSchedule?.specifiedPickUpTime ?? x.dropoffLatestDateTime,
          );
          const taskEnd = moment.utc(
            x.taskSupplierSchedule?.specifiedDropOffTime ?? x.dropoffLatestDateTime,
          );
          return moment(calendarEventData.start).isBetween(taskStart, taskEnd, 'day', '[]');
        })
      ) {
        this.messageService.clear();
        this.messageService.add(
          Message.Error('Cannot change availability as it would conflict with assigned tasks'),
        );
        return;
      }

      const existingIndex = this.supplierSchedule.supplierAvailabilityExceptions.findIndex(
        (ex) =>
          ex.id === calendarEventData.eventId &&
          moment(ex.exceptionDate).isSame(calendarEventData.start, 'day'),
      );

      if (existingIndex > -1) {
        if (nextState === SupplierAvailabilityStateEnum.unset) {
          this.supplierSchedule.supplierAvailabilityExceptions.splice(existingIndex, 1);
        } else {
          this.supplierSchedule.supplierAvailabilityExceptions[existingIndex].availabilityState =
            nextState;
        }
      } else {
        this.supplierSchedule.supplierAvailabilityExceptions.push({
          availabilityState: nextState,
          exceptionDate: moment.utc(calendarEventData.start).toDate(),
          id: -1,
        });
      }

      this.save().then(() => this.calendarViewChangedSubject.next(this.availabilityFilterVal));
    } else if (
      calendarEventData.extendedProps.calendarEventType === CalendarEventType.SupplierTask &&
      calendarEventData.extendedProps.jobId
    ) {
      this.router.navigate([]).then(() => {
        window.open(`app/jobscreen/${calendarEventData.extendedProps.jobId}`, '_blank');
      });
    }
  }

  private isVMEUser() {
    return !!this.supplierId;
  }

  calendarDateClicked(dateRange: any) {
    if (!this.supplierSchedule) return;

    const startDate = moment(dateRange.start);
    const endDate = moment(dateRange.end);
    const durationDays = Math.abs(moment.duration(startDate.diff(endDate)).asDays());

    if (durationDays > 1) {
      this.handleDateRangeSelection(startDate, endDate);
    } else {
      this.handleSingleDateSelection(startDate);
    }
  }

  private handleDateRangeSelection(startDate: moment.Moment, endDate: moment.Moment) {
    this.selectedDateRangeStart = startDate.toDate();
    this.selectedDateRangeEnd = endDate.toDate();
    this.selectedDateRangeEndDisplay = endDate.subtract(1, 'second').toDate();
    this.showRangeSelectionDialog = true;
  }

  private handleSingleDateSelection(date: moment.Moment) {
    const existingIndex = this.supplierSchedule.supplierAvailabilityExceptions.findIndex((ex) =>
      moment(ex.exceptionDate).isSame(date, 'day'),
    );

    const currentState =
      existingIndex > -1
        ? this.supplierSchedule.supplierAvailabilityExceptions[existingIndex].availabilityState
        : SupplierAvailabilityStateEnum.unset;

    // vme users can't unset dnd state
    const isVME = this.isVMEUser();
    if (isVME && currentState == SupplierAvailabilityStateEnum.doNotDisturb) {
      return;
    }

    const nextState = this.getNextState(currentState);

    // cannot set DND as the state when there are tasks on the date
    if (
      (nextState == SupplierAvailabilityStateEnum.doNotDisturb ||
        nextState == SupplierAvailabilityStateEnum.unavailable) &&
      this.supplierSchedule.supplierTasks.some((x) => {
        const taskStart = moment.utc(
          x.taskSupplierSchedule?.specifiedPickUpTime ?? x.dropoffLatestDateTime,
        );
        const taskEnd = moment.utc(
          x.taskSupplierSchedule?.specifiedDropOffTime ?? x.dropoffLatestDateTime,
        );
        return moment(date).isBetween(taskStart, taskEnd, 'day', '[]');
      })
    ) {
      this.messageService.clear();
      this.messageService.add(
        Message.Error(
          "Cannot set date to 'Do Not Disturb' as it would conflict with assigned tasks",
        ),
      );
      return;
    }

    if (existingIndex > -1) {
      if (nextState === SupplierAvailabilityStateEnum.unset) {
        this.supplierSchedule.supplierAvailabilityExceptions.splice(existingIndex, 1);
      } else {
        this.supplierSchedule.supplierAvailabilityExceptions[existingIndex].availabilityState =
          nextState;
      }
    } else {
      this.supplierSchedule.supplierAvailabilityExceptions.push({
        availabilityState: nextState,
        exceptionDate: moment.utc(date).toDate(),
        id: -1,
      });
    }

    this.save().then(() => this.calendarViewChangedSubject.next(this.availabilityFilterVal));
  }
  setDateRangeAvailability(state: SupplierAvailabilityStateEnum) {
    const startDate = moment(this.selectedDateRangeStart);
    const endDate = moment(this.selectedDateRangeEnd);
    const dateRangeLength = Math.abs(moment.duration(startDate.diff(endDate)).asDays());

    Array(dateRangeLength)
      .fill(null)
      .forEach((_, index) => {
        const currentDate = moment(this.selectedDateRangeStart).add(index, 'days');
        const existingIndex = this.supplierSchedule.supplierAvailabilityExceptions.findIndex((ex) =>
          moment(ex.exceptionDate).isSame(currentDate, 'day'),
        );

        if (existingIndex > -1) {
          if (state === SupplierAvailabilityStateEnum.unset) {
            this.supplierSchedule.supplierAvailabilityExceptions.splice(existingIndex, 1);
          } else {
            this.supplierSchedule.supplierAvailabilityExceptions[existingIndex].availabilityState =
              state;
          }
        } else if (state !== SupplierAvailabilityStateEnum.unset) {
          this.supplierSchedule.supplierAvailabilityExceptions.push({
            availabilityState: state,
            exceptionDate: moment.utc(currentDate).toDate(),
            id: -1,
          });
        }
      });

    this.showRangeSelectionDialog = false;
    this.save().then(() => this.calendarViewChangedSubject.next(this.availabilityFilterVal));
  }

  async save() {
    const exceptions = this.supplierSchedule.supplierAvailabilityExceptions.map((ex) => ({
      availabilityState: ex.availabilityState,
      exceptionDate: moment.utc(ex.exceptionDate).toDate(),
      id: ex.id,
    }));

    const availability: ISupplierSchedule = <ISupplierSchedule>{
      supplierId: this.supplierId || this.supplierSchedule.id,
      supplierAvailabilityExceptions: exceptions,
      validity: this.availabilityFilterVal.filters.toDate.value,
      fromDateTime: this.availabilityFilterVal.filters.fromDate.value,
      toDateTime: this.availabilityFilterVal.filters.toDate.value,
    };

    try {
      const res = await this.scheduleService.setSupplierAvailability(availability);
      if (res) {
        this.messageService.clear();
        this.messageService.add(Message.Success('Your availability information has been updated'));
      }
      return res;
    } catch (error) {
      this.messageService.add(Message.Error('Failed to update availability'));
      return false;
    }
  }

  navigateToSupplierSchedule() {
    if (this.supplierId) {
      this.router.navigate(['/app/supplier-availability-dashboard']);
    } else {
      this.router.navigate(['/app/supplieravailability']);
    }
  }
}
